import { Card } from "@/components/ui/card";
import { Target, Eye, User, MapPin, Award } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/brand";
import SectionHeader from "@/components/site/SectionHeader";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About RK Enterprises"
        title="Building reliable workforce partnerships across India"
        description="RK Enterprises specializes in manpower contracting and workforce supply solutions tailored to operational needs across industries."
        image={IMAGES.corporateMeeting}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="rk-eyebrow">Our story</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">
                A trusted partner for factories, housekeeping, and industrial operations
              </h2>
              <div className="mt-6 space-y-5 text-[#475569] leading-relaxed">
                <p>
                  {BRAND.name} specializes in manpower contracting and workforce
                  supply solutions tailored to operational needs across
                  industries. We partner with multiple companies to provide
                  reliable staffing solutions for factories, housekeeping,
                  logistics, and shift-based industrial roles.
                </p>
                <p>
                  Our approach combines disciplined recruitment, structured
                  welfare management (PF, ESI, accommodation, meals) and full
                  statutory compliance — so client companies get a workforce
                  that performs and stays.
                </p>
                <p>
                  Headquartered in Avadi, Chennai, RK Enterprises serves
                  manufacturing, automotive, logistics, and industrial sectors
                  with both short-term and long-term staffing engagements.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Stat
                  num="500+"
                  label="Workers placed"
                  icon={<User className="h-4 w-4" />}
                />
                <Stat
                  num="20+"
                  label="Client companies"
                  icon={<Award className="h-4 w-4" />}
                />
                <Stat
                  num="Pan-India"
                  label="Deployment reach"
                  icon={<MapPin className="h-4 w-4" />}
                />
              </div>
            </div>

            <div className="lg:col-span-5">
              <Card
                data-testid="proprietor-card"
                className="p-6 sm:p-8 bg-white border border-[#E2E8F0] rounded-sm rk-lift"
              >
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-sm bg-[#1E3A8A] grid place-items-center font-display text-white text-2xl font-extrabold">
                    RK
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#1F6B5C] font-bold">
                      Proprietor
                    </div>
                    <div className="font-display text-xl font-bold text-[#0F172A]">
                      {BRAND.proprietor}
                    </div>
                  </div>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-[#475569]">
                  Mr. Ramesh Krishnan V founded RK Enterprises with a clear
                  mission — to deliver disciplined, compliant, and dependable
                  manpower across industries. Under his leadership, the
                  enterprise has built long-term relationships with multiple
                  client companies through ethical workforce practices.
                </p>
                <div className="mt-6 pt-5 border-t border-[#E2E8F0]">
                  <div className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-2">
                    Headquartered at
                  </div>
                  <div className="text-sm text-[#0F172A]">
                    {BRAND.address.line1}
                    <br />
                    {BRAND.address.line2}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Mission & Vision"
            title="What drives us forward"
            align="center"
            testid="mv-header"
          />
          <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
            <Card
              data-testid="mission-card"
              className="rk-lift p-8 bg-white border border-[#E2E8F0] rounded-sm"
            >
              <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#1E3A8A] text-white">
                <Target className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold text-[#0F172A]">
                Mission
              </h3>
              <p className="mt-3 text-[#475569] leading-relaxed">
                To deliver disciplined, efficient, and compliant manpower
                solutions that help businesses operate smoothly. We commit to
                ethical recruitment, full statutory compliance, and worker
                welfare in every engagement.
              </p>
            </Card>
            <Card
              data-testid="vision-card"
              className="rk-lift p-8 bg-white border-2 border-[#1F6B5C] rounded-sm"
            >
              <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#1F6B5C] text-white">
                <Eye className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold text-[#0F172A]">
                Vision
              </h3>
              <p className="mt-3 text-[#475569] leading-relaxed">
                To become a trusted manpower solutions leader across India —
                recognized for reliability, discipline, and the ability to
                scale workforce deployments across multiple industries
                seamlessly.
              </p>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ num, label, icon }) {
  return (
    <div className="p-4 bg-[#F8FAFC] rounded-sm border border-[#E2E8F0]">
      <div className="flex items-center gap-1.5 text-[#1F6B5C]">
        {icon}
        <div className="font-display text-2xl font-extrabold text-[#0F172A]">
          {num}
        </div>
      </div>
      <div className="text-xs text-[#64748B] mt-1">{label}</div>
    </div>
  );
}

function PageHeader({ eyebrow, title, description, image }) {
  return (
    <section
      data-testid="page-header"
      className="relative overflow-hidden bg-[#0F172A]"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/80 to-[#0F172A]/40" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="rk-eyebrow !text-[#67e8c4]">{eyebrow}</div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight max-w-3xl">
          {title}
        </h1>
        {description && (
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl">
            {description}
          </p>
        )}
      </div>
    </section>
  );
}
